import { useState, useEffect, useContext } from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    IconButton,
    Typography,
    Divider,
    Button,
    CircularProgress,
} from '@mui/material';
import {
    Close as CloseIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Restore as RestoreIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../sporttia/AppContext';
import {
    SttSelectInterval,
    SttFormPrice,
    SttFormText,
    SttFormSelect,
} from '../../../sporttia/all';
import { SttGroupList } from '../../../sporttia/lists/SttGroupList';
import { getErrorMessage, parsePrice, subsetObject } from '../../../lib/utils';
import useScsService from '../../../services/ScsService';
import { PERMISSIONS } from '../../../lib/permissions';
import { usePermission } from '../../../lib/usePermission';

type Props = {
    item: any;
    onClose: () => void;
};

export default function Rate({ item, onClose }: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { can } = usePermission();
    const [rate, setRate] = useState<any>(null);

    const isCreating = item.id === 'create';

    useEffect(() => {
        if (isCreating) {
            setRate({
                name: '',
                individual: 0,
                prices: [{ duration: 60, price: 0 }],
                groups: [],
                position: 0,
            });
        } else {
            loadRate();
        }
    }, [item]);

    function loadRate(params = { trash: 1 }) {
        cxt?.api('GET', `/rates/${item.id}`, {
            params,
            success: (response: any) => {
                if (response.rate) {
                    setRate(response.rate);
                }
            },
        });
    }

    function onChange({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) {
        if (!name) return;
        setRate((prev: any) => ({ ...prev, [name]: value }));
    }

    function onChangeRateType({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) {
        if (!name) return;
        setRate((prev: any) => ({
            ...prev,
            [name]: parseInt(value as string),
        }));
    }

    function changePrice(price: any, value: any) {
        const { prices } = rate;
        const foundPrice = prices.find((p: any) => p === price);
        foundPrice.duration = value.duration;
        foundPrice.price = value.price;
        prices.splice(
            prices.findIndex((p: any) => p === price),
            1,
            foundPrice,
        );
        setRate({ ...rate, prices });
    }

    const scsService = useScsService();
    const updateScRateMutation = scsService.useUpdateScRate({
        onSuccess: () => {
            cxt?.showMessage('S', t('Saved'));
        },
        onError: (error: any) => {
            cxt?.showMessage('E', getErrorMessage(error));
        },
    });
    const createScRateMutation = scsService.useCreateScRate({
        onSuccess: (response: any) => {
            setRate(response.rate);
            cxt?.showMessage('S', t('Saved'));
        },
        onError: (error: any) => {
            cxt?.showMessage('E', getErrorMessage(error));
        },
    });

    function upsertRate() {
        const params = subsetObject(rate, [
            'name',
            'individual',
            'prices',
            'position',
        ]) as any;
        if (!params.prices[0].duration || params.prices[0].duration <= 0) {
            cxt?.showMessage('E', t('generic.durationRequired'));
            return;
        }
        if (rate.id) {
            updateScRateMutation.mutate({
                rateId: rate.id,
                params: {
                    ...params,
                    prices: [
                        {
                            ...params.prices[0],
                            price: parsePrice(params.prices[0].price),
                            duration: Number(params.prices[0].duration) || 0,
                        },
                    ],
                    groups: rate.groups,
                },
            } as any);
        } else {
            createScRateMutation.mutate({
                scId: Number(cxt?.sc?.id) || 0,
                params: {
                    ...params,
                    prices: [
                        {
                            ...params.prices[0],
                            price: parsePrice(params.prices[0].price),
                            duration: Number(params.prices[0].duration) || 0,
                        },
                    ],
                    groups: rate.groups,
                },
            } as any);
        }
    }

    function deleteRate() {
        cxt?.api('DELETE', `/rates/${rate.id}`, {
            confirmation: true,
            success: () => {
                onClose();
            },
        });
    }

    function recoverRate() {
        cxt?.api('PUT', `/rates/${rate.id}/recover`, {
            confirmation: true,
            success: () => {
                setRate({ ...rate, trash: 0 });
            },
        });
    }

    function addGroup(group: any) {
        cxt?.api('POST', `/rates/${rate.id}/groups/${group.id}`, {
            params: { idFacility: group.id },
            success: () => {
                setRate({
                    ...rate,
                    groups: rate.groups ? rate.groups.concat([group]) : [group],
                });
            },
        });
    }

    function removeGroup(group: any) {
        cxt?.api('DELETE', `/rates/${rate.id}/groups/${group.id}`, {
            success: () => {
                setRate({
                    ...rate,
                    groups: rate.groups.filter((g: any) => g.id !== group.id),
                });
            },
        });
    }

    const isSaving =
        createScRateMutation.status === 'pending' ||
        updateScRateMutation.status === 'pending';

    const handleClose = () => {
        setRate(null);
        onClose();
    };

    return (
        <Dialog
            open
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1,
                }}
            >
                <Typography variant="h6" component="span" fontWeight={600}>
                    {isCreating ? t('Rate') : rate?.name || t('Rate')}
                </Typography>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 3 }}>
                {rate && (
                    <Grid container spacing={3}>
                        {/* Detail - Left side */}
                        <Grid size={{ xs: 12, md: rate.id ? 5 : 12 }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                    <SttFormText
                                        name="name"
                                        caption={t('Name')}
                                        defVal={rate.name}
                                        onChange={onChange}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <SttFormSelect
                                        name="individual"
                                        defVal={rate.individual ? 1 : 0}
                                        caption={t('Type')}
                                        options={[
                                            {
                                                caption: t('Completed'),
                                                value: '0',
                                            },
                                            {
                                                caption: t('Individual'),
                                                value: '1',
                                            },
                                        ]}
                                        onChange={onChangeRateType}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <SttFormText
                                        name="position"
                                        caption={t('Position')}
                                        defVal={rate.position}
                                        onChange={onChange}
                                    />
                                </Grid>

                                {rate.prices && rate.prices[0] && (
                                    <>
                                        <Grid size={{ xs: 6 }}>
                                            <SttSelectInterval
                                                caption={t('Duration')}
                                                labelWidth={90}
                                                defVal={rate.prices[0].duration}
                                                min={5}
                                                max={1435}
                                                interval={5}
                                                onChange={(value: number) =>
                                                    changePrice(
                                                        rate.prices[0],
                                                        {
                                                            duration: value,
                                                            price: rate
                                                                .prices[0]
                                                                .price,
                                                        },
                                                    )
                                                }
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <SttFormPrice
                                                name="price"
                                                caption={t('Price')}
                                                defVal={rate.prices[0].price}
                                                onChange={({
                                                    name: _name,
                                                    value,
                                                }: {
                                                    name: string | undefined;
                                                    value: unknown;
                                                }) =>
                                                    changePrice(
                                                        rate.prices[0],
                                                        {
                                                            duration:
                                                                rate.prices[0]
                                                                    .duration,
                                                            price: value,
                                                        },
                                                    )
                                                }
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Grid>

                        {/* Group list - Right side */}
                        {rate.id && (
                            <Grid size={{ xs: 12, md: 7 }}>
                                <Box
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 2,
                                        height: '100%',
                                        minHeight: 200,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                        gutterBottom
                                    >
                                        {t('Groups')}
                                    </Typography>
                                    <SttGroupList
                                        groups={rate.groups || []}
                                        onAdd={(group: any) => addGroup(group)}
                                        onDelete={(group: any) =>
                                            removeGroup(group)
                                        }
                                    />
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 3, py: 2 }}>
                {rate && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            width: '100%',
                            justifyContent: 'flex-end',
                        }}
                    >
                        {rate.trash ? (
                            can(PERMISSIONS.RATE.DELETE) && (
                                <Button
                                    variant="contained"
                                    color="warning"
                                    onClick={recoverRate}
                                    startIcon={<RestoreIcon />}
                                >
                                    {t('Recover')}
                                </Button>
                            )
                        ) : (
                            <>
                                {rate.id && can(PERMISSIONS.RATE.DELETE) && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={deleteRate}
                                        startIcon={<DeleteIcon />}
                                    >
                                        <span>{t('Delete')}</span>
                                    </Button>
                                )}
                                {can(
                                    rate.id
                                        ? PERMISSIONS.RATE.MODIFY
                                        : PERMISSIONS.RATE.CREATE,
                                ) && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={upsertRate}
                                        disabled={isSaving}
                                        startIcon={
                                            isSaving ? (
                                                <CircularProgress
                                                    size={20}
                                                    color="inherit"
                                                />
                                            ) : (
                                                <SaveIcon />
                                            )
                                        }
                                    >
                                        <span>{t('Save')}</span>
                                    </Button>
                                )}
                            </>
                        )}
                    </Box>
                )}
            </DialogActions>
        </Dialog>
    );
}
